import { useEffect, useState } from 'react';
import { useTranslation } from '@/i18n';
import { SlideOver, SlideOverFooter } from '@/components/Shared/SlideOver';
import { FormField } from '@/components/Shared/FormField';
import { useVehicleStore } from '@/store/useVehicleStore';
import type { VehicleDto } from '@/store/useVehicleStore';
import { MAX_ODOMETER_KM } from '@/utils/logbookLimits';
import VehicleChecksSection from '@/pages/Vehicles/VehicleChecksSection';

const MAX_LICENSE_PLATE = 20;
const MAX_DESCRIPTION = 200;

interface VehicleFormProps {
  isOpen: boolean;
  onClose: () => void;
  /** null = create a new vehicle. */
  vehicle: VehicleDto | null;
}

/** Empty → null (no reading); otherwise whole kilometres, or undefined when invalid. */
function parseOdometer(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const km = Number(trimmed);
  return km <= MAX_ODOMETER_KM ? km : undefined;
}

/**
 * Create or edit a vehicle. The license plate is fixed once the vehicle exists — the logbook
 * refers to it (FAFD-26). A changed odometer reading is stored as a correction in the logbook.
 */
export default function VehicleForm({ isOpen, onClose, vehicle }: VehicleFormProps) {
  const { t } = useTranslation(['vehicles', 'common']);
  const createVehicle = useVehicleStore((s) => s.createVehicle);
  const updateVehicle = useVehicleStore((s) => s.updateVehicle);
  const saving = useVehicleStore((s) => s.saving);

  const [licensePlate, setLicensePlate] = useState('');
  const [description, setDescription] = useState('');
  const [odometer, setOdometer] = useState('');
  const [plateError, setPlateError] = useState<string | undefined>();
  const [odometerError, setOdometerError] = useState<string | undefined>();
  const [touched, setTouched] = useState(false);

  const isEdit = Boolean(vehicle?.id);
  const currentKm = vehicle?.currentOdometerKm ?? null;

  useEffect(() => {
    if (!isOpen) return;
    setLicensePlate(vehicle?.licensePlate ?? '');
    setDescription(vehicle?.description ?? '');
    setOdometer(vehicle?.currentOdometerKm != null ? String(vehicle.currentOdometerKm) : '');
    setPlateError(undefined);
    setOdometerError(undefined);
    setTouched(false);
  }, [isOpen, vehicle]);

  const handleSubmit = async () => {
    const plate = licensePlate.trim();
    if (!isEdit && !plate) {
      setPlateError(t('vehicles:validation.licensePlateRequired'));
      return;
    }
    const km = parseOdometer(odometer);
    if (km === undefined) {
      setOdometerError(t('vehicles:validation.odometerInvalid'));
      return;
    }

    const trimmedDescription = description.trim() || null;
    const saved = vehicle?.id
      ? await updateVehicle(vehicle.id, {
          description: trimmedDescription,
          isActive: vehicle.isActive ?? true,
          // Only a changed value becomes a correction entry.
          odometerKm: km !== null && km !== currentKm ? km : null,
        })
      : await createVehicle({ licensePlate: plate, description: trimmedDescription, initialOdometerKm: km });
    if (saved) onClose();
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={vehicle ? t('vehicles:actions.edit') : t('vehicles:actions.create')}
      size="md"
      hasUnsavedChanges={touched && !saving}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={handleSubmit}
          confirmText={t('common:actions.save')}
          cancelText={t('common:actions.cancel')}
          loading={saving}
        />
      }
    >
      <div className="space-y-5">
        <FormField
          label={t('vehicles:fields.licensePlate')}
          name="licensePlate"
          required={!isEdit}
          value={licensePlate}
          disabled={isEdit}
          readOnly={isEdit}
          onChange={(e) => {
            setLicensePlate(e.target.value.toUpperCase());
            setTouched(true);
            setPlateError(undefined);
          }}
          maxLength={MAX_LICENSE_PLATE}
          placeholder={t('vehicles:placeholders.licensePlate')}
          helperText={isEdit ? t('vehicles:hints.licensePlateFixed') : undefined}
          error={plateError}
          className="font-mono"
        />
        <FormField
          label={t('vehicles:fields.description')}
          name="description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            setTouched(true);
          }}
          maxLength={MAX_DESCRIPTION}
          placeholder={t('vehicles:placeholders.description')}
        />
        <FormField
          label={isEdit ? t('vehicles:fields.currentOdometer') : t('vehicles:fields.initialOdometer')}
          name="odometer"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={odometer}
          onChange={(e) => {
            setOdometer(e.target.value.replace(/\D/g, ''));
            setTouched(true);
            setOdometerError(undefined);
          }}
          placeholder={t('vehicles:placeholders.odometer')}
          helperText={isEdit ? t('vehicles:hints.odometerCorrection') : t('vehicles:hints.initialOdometer')}
          error={odometerError}
          className="font-mono tabular-nums"
        />
        {vehicle?.id && <VehicleChecksSection vehicleId={vehicle.id} />}
      </div>
    </SlideOver>
  );
}
